import { paymentAPI } from '../../services/api';
import { responseData } from '../../services/apiResponse';

export function paymentContinueCacheKey(requestId) {
  return `payment_continue:${requestId}`;
}

function normalizeSelectionValue(value) {
  return (value || '').trim().toUpperCase();
}

function checkoutSelection(input) {
  return {
    sender_country: normalizeSelectionValue(input.sender_country),
    fiat_currency: normalizeSelectionValue(input.fiat_currency),
  };
}

function numberFromStorage(key) {
  const value = Number(localStorage.getItem(key));
  return Number.isFinite(value) && value > 0 ? value : undefined;
}

function checkoutContainsCredential(checkout) {
  for (const value of [checkout?.checkout_url, checkout?.widget_url]) {
    if (!value) continue;
    try {
      const params = new URL(value, window.location.origin).searchParams;
      if (params.has('kycToken') || params.has('kycShareToken') || params.has('access_token')) return true;
    } catch {
      return true;
    }
  }
  return false;
}

export function buildContinuePaymentInput(request) {
  return {
    expected_active_attempt_id: request.active_attempt_id || '',
    sender_country: localStorage.getItem('selected_country_alpha2') || '',
    provider: (process.env.REACT_APP_ONRAMP_PROVIDER || '').trim().toUpperCase() || undefined,
    purpose_code: localStorage.getItem('purpose_code') || undefined,
    confirmed_crypto_amount: numberFromStorage('confirmed_amount'),
    fiat_currency: localStorage.getItem('fiat_currency') || undefined,
    region_code: localStorage.getItem('selected_region_code') || undefined,
    payment_method: localStorage.getItem('payment_method') || undefined,
  };
}

export function createIdempotencyKey() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `continue-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export async function executeContinuePayment(request) {
  const input = buildContinuePaymentInput(request);
  const response = await paymentAPI.continuePaymentRequest(
    request.request_id,
    input,
    createIdempotencyKey()
  );
  const checkout = responseData(response)?.checkout;
  if (!checkout?.checkout_url && !checkout?.widget_url) {
    throw new Error('The payment provider did not return an available checkout.');
  }
  if (!checkoutContainsCredential(checkout)) {
    sessionStorage.setItem(paymentContinueCacheKey(request.request_id), JSON.stringify({
      checkout,
      action: responseData(response).action,
      selection: checkoutSelection(input),
      stored_at: Date.now(),
    }));
  }
  return responseData(response);
}

export function readContinuePaymentCheckout(requestId) {
  try {
    const cacheKey = paymentContinueCacheKey(requestId);
    const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
    const currentSelection = checkoutSelection({
      sender_country: localStorage.getItem('selected_country_alpha2'),
      fiat_currency: localStorage.getItem('fiat_currency'),
    });
    const selectionMatches =
      cached?.selection?.sender_country === currentSelection.sender_country &&
      cached?.selection?.fiat_currency === currentSelection.fiat_currency;
    const rawCheckoutExpiry = cached?.checkout?.checkout_expires_at;
    const checkoutExpiry = rawCheckoutExpiry ? Date.parse(rawCheckoutExpiry) : null;
    const checkoutExpired = rawCheckoutExpiry &&
      (!Number.isFinite(checkoutExpiry) || Date.now() >= checkoutExpiry);
    if (
      !cached?.checkout ||
      !selectionMatches ||
      checkoutExpired ||
      Date.now() - Number(cached.stored_at || 0) > 30 * 60 * 1000
    ) {
      sessionStorage.removeItem(cacheKey);
      return null;
    }
    return cached.checkout;
  } catch {
    return null;
  }
}
